import React, { useState } from 'react';
import { Globe, Play, Loader2, UploadCloud, AlertCircle, Filter, Copy } from 'lucide-react';
import axios from 'axios';

export default function DnsCheckerPage() {
  const [domainInput, setDomainInput] = useState(() => {
    return localStorage.getItem('dns_checker_last_search') || '';
  });
  const [results, setResults] = useState(() => {
    const saved = localStorage.getItem('dns_checker_last_results');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [visibleColumns, setVisibleColumns] = useState({
    A: true,
    MX: true,
    TXT: true,
    SPF: true,
    DMARC: true
  });
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedText, setCopiedText] = useState(null);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1500);
  };

  const handleCopyAll = () => {
    const headers = ['Domain'];
    if (visibleColumns.A) headers.push('A Record');
    if (visibleColumns.MX) headers.push('MX Record');
    if (visibleColumns.TXT) headers.push('TXT Record');
    if (visibleColumns.SPF) headers.push('SPF Record');
    if (visibleColumns.DMARC) headers.push('DMARC Record');

    const csvRows = results.map(r => {
      const row = [r.domain];
      if (visibleColumns.A) row.push(`"${(r.A || '').replace(/"/g, '""')}"`);
      if (visibleColumns.MX) row.push(`"${(r.MX || '').replace(/"/g, '""')}"`);
      if (visibleColumns.TXT) row.push(`"${(r.TXT || '').replace(/"/g, '""')}"`);
      if (visibleColumns.SPF) row.push(`"${(r.SPF || '').replace(/"/g, '""')}"`);
      if (visibleColumns.DMARC) row.push(`"${(r.DMARC || '').replace(/"/g, '""')}"`);
      return row.join('\t');
    });

    const fullText = [headers.join('\t'), ...csvRows].join('\n');
    navigator.clipboard.writeText(fullText);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      setDomainInput(evt.target.result);
    };
    reader.onerror = () => {
      setError("Failed to read file.");
    };
    reader.readAsText(file);
    e.target.value = null; // Reset input
  };

  const handleCheck = async () => {
    setError('');
    const domains = domainInput
      .split('\n')
      .map(d => d.trim().toLowerCase())
      .filter(d => d && !d.includes(' '));

    if (domains.length === 0) {
      setError('Please provide at least one valid domain.');
      return;
    }

    setIsLoading(true);
    setResults([]); // Clear previous results
    
    try {
      let token = localStorage.getItem('token');
      // If token is wrapped in quotes from JSON.stringify, remove them
      if (token && token.startsWith('"') && token.endsWith('"')) {
          token = token.slice(1, -1);
      }

      if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.post('https://api.ymoghar.engineer/api_v1.0/dns/check', 
        { domains },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setResults(response.data);
      localStorage.setItem('dns_checker_last_results', JSON.stringify(response.data));
      localStorage.setItem('dns_checker_last_search', domainInput);
    } catch (err) {
      console.error("DNS Check Error:", err);
      // Handle axios error format
      const errorMessage = err.response?.data?.message 
        || err.response?.data?.detail 
        || `Authentication error or Server failed (${err.response?.status})`;
        
      setError(errorMessage || 'Failed to fetch DNS records from the server.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center mb-4 gap-2">
        <div className="bg-primary text-white p-2 rounded d-flex align-items-center justify-content-center">
          <Globe size={24} />
        </div>
        <h4 className="fw-bold m-0 text-dark">DNS Checker</h4>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 mb-4 border-0 shadow-sm">
            <AlertCircle size={18} /> {error}
        </div>
      )}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12 mb-2">
              <label className="form-label small fw-bold text-uppercase text-muted">Domains List (One per line)</label>
              <textarea 
                className="form-control border-0 bg-light shadow-none" 
                rows="6"
                placeholder="example.com&#10;test.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                style={{ fontSize: '13px', fontFamily: 'monospace' }}
                disabled={isLoading}
              />
            </div>
            
            <div className="col-md-6 d-flex align-items-center">
              <label className="btn btn-outline-secondary d-flex align-items-center justify-content-center gap-2 m-0 shadow-sm w-100 py-2" style={{ cursor: 'pointer' }}>
                <UploadCloud size={18} />
                Upload Text File
                <input 
                  type="file" 
                  accept=".txt" 
                  className="d-none" 
                  onChange={handleFileUpload}
                  disabled={isLoading}
                />
              </label>
            </div>
            
            <div className="col-md-6">
              <button 
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                onClick={handleCheck}
                disabled={isLoading || !domainInput.trim()}
              >
                {isLoading ? <Loader2 size={18} className="spin-animation" /> : <Play size={18} />}
                {isLoading ? 'Checking DNS Records...' : 'Check DNS Records'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {results.length > 0 && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white py-3 border-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <h6 className="fw-bold m-0 d-flex align-items-center gap-2">
                <Globe size={18} className="text-primary" />
                Results
              </h6>
              <button 
                className={`btn btn-sm ${copiedAll ? 'btn-success' : 'btn-outline-primary'} d-flex align-items-center gap-1`}
                onClick={handleCopyAll}
              >
                <Copy size={14} />
                {copiedAll ? 'Copied!' : 'Copy Table'}
              </button>
              {copiedText && (
                <span className="badge bg-success bg-opacity-10 text-success border border-success">
                  Copied: {copiedText.length > 20 ? copiedText.substring(0, 20) + '...' : copiedText}
                </span>
              )}
            </div>
            
            <div className="d-flex align-items-center flex-wrap gap-3 p-2 bg-light rounded border">
              <div className="d-flex align-items-center gap-1 text-muted">
                <Filter size={16} />
                <span className="small fw-semibold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Columns:</span>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {['A', 'MX', 'TXT', 'SPF', 'DMARC'].map(col => (
                  <div key={col} className="form-check form-switch m-0 d-flex align-items-center gap-2">
                    <input 
                      className="form-check-input mt-0 cursor-pointer shadow-none" 
                      type="checkbox" 
                      id={`toggle-${col}`}
                      checked={visibleColumns[col]}
                      onChange={() => setVisibleColumns(prev => ({...prev, [col]: !prev[col]}))}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label small fw-medium user-select-none" htmlFor={`toggle-${col}`} style={{ cursor: 'pointer' }}>
                      {col}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
              <thead className="table-light">
                <tr>
                  <th className="fw-semibold text-uppercase py-3 ps-4">Domain</th>
                  {visibleColumns.A && <th className="fw-semibold text-uppercase py-3">A Record</th>}
                  {visibleColumns.MX && <th className="fw-semibold text-uppercase py-3" style={{ minWidth: '150px' }}>MX Record</th>}
                  {visibleColumns.TXT && <th className="fw-semibold text-uppercase py-3" style={{ minWidth: '250px' }}>TXT Record</th>}
                  {visibleColumns.SPF && <th className="fw-semibold text-uppercase py-3" style={{ minWidth: '250px' }}>SPF Record</th>}
                  {visibleColumns.DMARC && <th className="fw-semibold text-uppercase py-3 pe-4" style={{ minWidth: '250px' }}>DMARC Record</th>}
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr key={idx}>
                    <td className="ps-4 fw-medium">{r.domain}</td>
                    {visibleColumns.A && (
                      <td>
                        <span 
                          className={`badge ${r.A === 'Not Found' || r.A === 'Error' ? 'bg-danger' : 'bg-success'} bg-opacity-10 ${r.A === 'Not Found' || r.A === 'Error' ? 'text-danger' : 'text-success'} border ${r.A === 'Not Found' || r.A === 'Error' ? 'border-danger' : 'border-success'}`}
                          onClick={() => copyToClipboard(r.A)}
                          style={{ cursor: 'pointer' }}
                          title="Click to copy"
                        >
                          {r.A}
                        </span>
                      </td>
                    )}
                    {visibleColumns.MX && (
                      <td>
                        <span 
                          className={`badge ${r.MX === 'Not Found' || r.MX === 'Error' ? 'bg-danger' : 'bg-success'} bg-opacity-10 ${r.MX === 'Not Found' || r.MX === 'Error' ? 'text-danger' : 'text-success'} border ${r.MX === 'Not Found' || r.MX === 'Error' ? 'border-danger' : 'border-success'} text-wrap`} 
                          style={{ textAlign: 'left', display: 'inline-block', cursor: 'pointer' }}
                          onClick={() => copyToClipboard(r.MX)}
                          title="Click to copy"
                        >
                          {r.MX}
                        </span>
                      </td>
                    )}
                    {visibleColumns.TXT && (
                      <td style={{ wordBreak: 'break-all' }}>
                        <span 
                          className={`badge ${r.TXT === 'Not Found' || r.TXT === 'Error' ? 'bg-danger text-white' : 'bg-light text-dark'} border text-wrap`} 
                          style={{ textAlign: 'left', display: 'inline-block', cursor: 'pointer' }}
                          onClick={() => copyToClipboard(r.TXT)}
                          title="Click to copy"
                        >
                          {r.TXT}
                        </span>
                      </td>
                    )}
                    {visibleColumns.SPF && (
                      <td style={{ wordBreak: 'break-all' }}>
                        <span 
                          className={`badge ${r.SPF === 'Not Found' || r.SPF === 'Error' ? 'bg-danger text-white' : r.SPF?.includes('⚠️ MULTIPLE:') ? 'bg-warning text-dark border-warning' : 'bg-light text-dark'} border text-wrap`} 
                          style={{ textAlign: 'left', display: 'inline-block', cursor: 'pointer' }}
                          onClick={() => copyToClipboard(r.SPF)}
                          title="Click to copy"
                        >
                          {r.SPF}
                        </span>
                      </td>
                    )}
                    {visibleColumns.DMARC && (
                      <td className="pe-4" style={{ wordBreak: 'break-all' }}>
                         <span 
                           className={`badge ${r.DMARC === 'Not Found' || r.DMARC === 'Error' ? 'bg-danger text-white' : 'bg-light text-dark'} border text-wrap`} 
                           style={{ textAlign: 'left', display: 'inline-block', cursor: 'pointer' }}
                           onClick={() => copyToClipboard(r.DMARC)}
                           title="Click to copy"
                         >
                          {r.DMARC}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
